import { useFloatingPlacement } from '@/lib/use-floating-placement';
import { useCallback, useEffect, useState } from 'react';

/**
 * Where a panel of a criterion's row stands, and what makes it go away (issue
 * #104; grill of 2026-09-21, D3 and D7).
 *
 * ## Why a hook rather than two copies
 *
 * The line of a cited criterion opens TWO panels - the fiche its name opens,
 * the version chooser its pill opens - and the grill gives both of them the
 * same two corrections: they must never be cropped, and they must close on
 * Escape and on a click outside. Everything else about them differs. Written
 * twice, the two would be kept in step by hand until the day one of them
 * stopped being; that is the lesson `lib/floating.ts` states in its own header
 * about the placement maths, and it applies just as well to the six lines of
 * listeners around it.
 *
 * What is NOT here is anything that would make the two panels the same thing:
 * no role, no focus trap, no markup. A fiche is a dialog one reads and a
 * chooser is a radiogroup one picks from (D7), and the caller says which.
 *
 * ## `position: fixed` and NOT a portal
 *
 * Both panels used to be `absolute top-full left-0`, which puts them inside
 * their row's positioning context and therefore inside every `overflow` above
 * it - the step editor scrolls, so the bottom of an objective cropped them.
 * `position: fixed` takes an element out of every ancestor's clipping without
 * moving it in the tree, which is the whole reason `variable-fiche.tsx` uses
 * it, and it is what this hook places.
 *
 * Leaving the node where it is in the DOM is deliberate rather than
 * incidental: `createPortal` would send the panel to `document.body`, and the
 * fiche mounted by `variable-fiche.tsx` is found by `useVariableFiche` through
 * `ficheRef(node).contains(target)` - a panel portalled away from its box
 * would read as « outside » and every click inside it would close the fiche
 * that carries it.
 *
 * ## The outside click listens on `mousedown`
 *
 * The reason `action-menu.tsx` gives: on `click` the very event that OPENS the
 * panel bubbles to the document and shuts it again in the same tick. A
 * `mousedown` inside the panel or on its own anchor is ignored, so pressing the
 * trigger a second time reaches the trigger's toggle rather than being eaten
 * here.
 */

export interface FloatingPanelRequest {
  /**
   * The element the panel stands against AND the control the focus returns to.
   * Null when the CALLER has already placed the panel - the fiche of a
   * criterion is also mounted inside `variable-fiche.tsx`, which is itself a
   * placed `fixed` box, and a panel placed twice would be placed wrong.
   */
  readonly anchor: HTMLElement | null;
  /** The panel's width, which the placement needs before the DOM has one. */
  readonly width: number;
  /** Escape, or a click outside. Never called while `anchor` is null. */
  readonly onClose: () => void;
}

export interface FloatingPanel {
  /** On the panel's own node: it is measured, and a click inside it is inside. */
  readonly panelRef: (node: HTMLElement | null) => void;
  /** Straight into `style` on a `position: fixed` element. */
  readonly at: { readonly left: number; readonly top: number };
}

export function useFloatingPanel({ anchor, width, onClose }: FloatingPanelRequest): FloatingPanel {
  /*
    The placement is NOT written here, and that is the point of issue 104's
    second review: there were four copies of this effect in the package, a
    defect was found on a capture, and it was fixed in one of them. Under the
    anchor and aligned on its left edge - what `top-full left-0` used to say,
    now said in viewport coordinates and free to flip up when the window ends.
  */
  const { at, panelRef: measured } = useFloatingPlacement({
    anchor,
    width,
    side: 'bottom',
    align: 'start',
  });
  const [box, setBox] = useState<HTMLElement | null>(null);
  const panelRef = useCallback(
    (node: HTMLElement | null) => {
      setBox(node);
      measured(node);
    },
    [measured],
  );

  useEffect(() => {
    if (anchor === null) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key !== 'Escape' || anchor === null) return;
      /*
        The focus goes back to the trigger BEFORE the panel is asked to close,
        the order `action-menu.tsx` holds: closing first unmounts the node the
        focus stands in, and the keyboard lands on `<body>` with the page's
        whole tab order to walk again.
      */
      anchor.focus();
      onClose();
    }
    function onDown(event: MouseEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || anchor === null) return;
      if (box?.contains(target) === true) return;
      if (anchor.contains(target)) return;
      onClose();
    }
    /*
      Le clavier ferme aussi, et c'est le second point de la revue.

      Entrée et Espace sur un bouton font synthétiser un `click` par le
      navigateur, JAMAIS un `mousedown` : au clavier, rien ne fermait donc le
      panneau précédent, et deux dialogues non modaux pouvaient se retrouver
      ouverts en même temps - un seul Échap les fermant tous les deux, chacun
      rendant le focus à sa propre ancre. Plus courant encore : un lecteur qui
      ouvre la fiche puis tabule ailleurs la laissait plantée en `fixed`
      par-dessus la page. `focusin` est le pendant clavier du clic dehors, et
      c'est ce que `action-menu.tsx` et `use-variable-fiche.ts` font déjà.
    */
    function onFocusIn(event: FocusEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || anchor === null) return;
      if (box?.contains(target) === true) return;
      if (anchor.contains(target)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [anchor, box, onClose]);

  return { panelRef, at };
}
